import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Loader2, MapPin, Package, Truck, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal — Demande de modification d'un transfert planifié (Phase 3a, PM Spec 07/05/2026)
 *
 * Champs autorisés (validés par PM) :
 *   - Adresses (départ, arrivée)
 *   - Marchandise (description, poids)
 *   - Type de véhicule
 *   - Commentaire libre obligatoire
 */
const RequestTransferModificationModal = ({ open, onOpenChange, contractId, runId, run, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState('');

  const [changeFlags, setChangeFlags] = useState({
    departure: false,
    arrival: false,
    merchandise: false,
    vehicle: false,
  });

  const [departure, setDeparture] = useState('');
  const [arrival, setArrival] = useState('');
  const [merchandise, setMerchandise] = useState('');
  const [weight, setWeight] = useState('');
  const [vehicleType, setVehicleType] = useState('');

  const reset = () => {
    setComment('');
    setDeparture(''); setArrival(''); setMerchandise(''); setWeight(''); setVehicleType('');
    setChangeFlags({ departure: false, arrival: false, merchandise: false, vehicle: false });
  };

  const submit = async () => {
    if (!comment || comment.trim().length < 5) {
      toast.error('Merci de décrire votre demande dans le commentaire (5 caractères min).');
      return;
    }
    const noField = !changeFlags.departure && !changeFlags.arrival
      && !changeFlags.merchandise && !changeFlags.vehicle;
    if (noField) {
      toast.error('Cochez au moins un type de modification souhaitée.');
      return;
    }

    const proposedChanges = { comment: comment.trim() };
    if (changeFlags.departure && departure.trim()) proposedChanges.departure = { address: departure.trim() };
    if (changeFlags.arrival && arrival.trim()) proposedChanges.arrival = { address: arrival.trim() };
    if (changeFlags.merchandise) {
      if (merchandise.trim()) proposedChanges.merchandiseDescription = merchandise.trim();
      if (weight) proposedChanges.weight = parseFloat(weight);
    }
    if (changeFlags.vehicle && vehicleType.trim()) proposedChanges.vehicleType = vehicleType.trim();

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/client/contracts/${contractId}/transfers/${runId}/modification-requests`,
        { proposedChanges },
        {},
      );
      toast.success('Demande envoyée à notre équipe', {
        description: 'Vous serez notifié dès qu\'elle aura été traitée.',
      });
      reset();
      onOpenChange?.(false);
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur lors de l\'envoi de la demande';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const runLabel = run?.runNumber || run?.operationNumber || runId?.slice(0, 8);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modif-request-modal">
        <DialogHeader>
          <DialogTitle>Demander une modification — Transfert {runLabel}</DialogTitle>
          <DialogDescription>
            Cochez les éléments à modifier puis décrivez votre demande. Notre équipe traitera votre demande sous peu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Adresses */}
          <div className="border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox
                id="chk-addr"
                checked={changeFlags.departure || changeFlags.arrival}
                onCheckedChange={(v) => setChangeFlags(f => ({ ...f, departure: !!v, arrival: !!v }))}
              />
              <Label htmlFor="chk-addr" className="font-semibold flex items-center gap-1.5 cursor-pointer">
                <MapPin className="w-4 h-4 text-emerald-600" /> Adresses
              </Label>
            </div>
            {(changeFlags.departure || changeFlags.arrival) && (
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-xs text-slate-500">Nouveau départ</Label>
                  <Input
                    value={departure}
                    onChange={(e) => setDeparture(e.target.value)}
                    placeholder={run?.departure || 'Ex: Abidjan, Marcory'}
                    data-testid="modif-departure"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Nouvelle arrivée</Label>
                  <Input
                    value={arrival}
                    onChange={(e) => setArrival(e.target.value)}
                    placeholder={run?.arrival || 'Ex: Bouaké, Kennedy'}
                    data-testid="modif-arrival"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Marchandise */}
          <div className="border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox
                id="chk-merch"
                checked={changeFlags.merchandise}
                onCheckedChange={(v) => setChangeFlags(f => ({ ...f, merchandise: !!v }))}
              />
              <Label htmlFor="chk-merch" className="font-semibold flex items-center gap-1.5 cursor-pointer">
                <Package className="w-4 h-4 text-blue-600" /> Marchandise
              </Label>
            </div>
            {changeFlags.merchandise && (
              <div className="space-y-2 mt-2">
                <div>
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Input
                    value={merchandise}
                    onChange={(e) => setMerchandise(e.target.value)}
                    placeholder={run?.merchandiseDescription || 'Ex: 200 sacs de café'}
                    data-testid="modif-merchandise"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Poids (tonnes)</Label>
                  <Input
                    type="number" step="0.1" min="0"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder={run?.weight || '5'}
                    data-testid="modif-weight"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Véhicule */}
          <div className="border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox
                id="chk-veh"
                checked={changeFlags.vehicle}
                onCheckedChange={(v) => setChangeFlags(f => ({ ...f, vehicle: !!v }))}
              />
              <Label htmlFor="chk-veh" className="font-semibold flex items-center gap-1.5 cursor-pointer">
                <Truck className="w-4 h-4 text-amber-600" /> Type de véhicule
              </Label>
            </div>
            {changeFlags.vehicle && (
              <Input
                className="mt-2"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                placeholder={run?.vehicleType || 'Ex: Camion 10T plateau'}
                data-testid="modif-vehicle"
              />
            )}
          </div>

          {/* Commentaire */}
          <div>
            <Label className="font-semibold flex items-center gap-1.5 mb-1">
              <MessageSquare className="w-4 h-4 text-slate-600" /> Commentaire * <span className="text-xs text-slate-400 font-normal">(obligatoire)</span>
            </Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Expliquez le contexte et l'urgence de votre demande…"
              data-testid="modif-comment"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange?.(false); }} disabled={submitting}>
            Annuler
          </Button>
          <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="modif-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Envoyer la demande
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestTransferModificationModal;
